import { useMemo } from 'react'

import { TAgent } from '~/features/agents/models'
import useGetCampaignScheduleSteps from '~/features/campaigns/hooks/useGetCampaignScheduleSteps'
import { ECampaignKeys, TCampaign } from '~/features/campaigns/models'
import {
  getGoLiveValidationErrorsWithSteps,
  TGoLiveValidationErrorWithStep,
} from '~/features/campaigns/utils'

type TOptions = {
  selectedAgent?: TAgent
  timezone?: string
}

export const SCHEDULE_STEPS_LOADING_MESSAGE = 'Loading campaign schedule…'

const SCHEDULE_STEPS_FETCH_ERROR: TGoLiveValidationErrorWithStep = {
  message: 'Could not load the campaign schedule. Please try again.',
}

/**
 * Go-live / simulate validation for manage flows. Loads schedule steps from the
 * dedicated API when the campaign uses a structured schedule (campaign GET does
 * not include them).
 */
function useGoLiveValidationErrorsWithSteps(
  campaign: TCampaign | Record<string, unknown> | null | undefined,
  options?: TOptions,
) {
  const campaignId = String(
    (campaign as TCampaign | null | undefined)?.[ECampaignKeys.id] || '',
  ).trim()
  const useStructuredSchedule = Boolean(
    (campaign as TCampaign | null | undefined)?.[
      ECampaignKeys.useStructuredSchedule
    ],
  )

  const {
    data: scheduleStepsResponse,
    isError: isScheduleStepsError,
    isSuccess: isScheduleStepsSuccess,
  } = useGetCampaignScheduleSteps(campaignId, {
    enabled: !!campaignId && useStructuredSchedule,
  })

  // Do not treat a failed fetch as an empty schedule (`isFetched` is true on error).
  const isScheduleStepsReady =
    !useStructuredSchedule || isScheduleStepsSuccess || isScheduleStepsError

  const scheduleSteps = useMemo(() => {
    if (!useStructuredSchedule || !isScheduleStepsSuccess) {
      return []
    }

    return scheduleStepsResponse?.data ?? []
  }, [
    isScheduleStepsSuccess,
    scheduleStepsResponse?.data,
    useStructuredSchedule,
  ])

  const { selectedAgent, timezone } = options ?? {}

  const validationErrorsWithSteps =
    useMemo((): TGoLiveValidationErrorWithStep[] => {
      if (!isScheduleStepsReady) {
        return []
      }

      if (useStructuredSchedule && isScheduleStepsError) {
        return [SCHEDULE_STEPS_FETCH_ERROR]
      }

      return getGoLiveValidationErrorsWithSteps(campaign, {
        scheduleSteps,
        selectedAgent,
        timezone,
      })
    }, [
      campaign,
      isScheduleStepsError,
      isScheduleStepsReady,
      scheduleSteps,
      selectedAgent,
      timezone,
      useStructuredSchedule,
    ])

  return {
    isScheduleStepsReady,
    validationErrorsWithSteps,
  }
}

export default useGoLiveValidationErrorsWithSteps
